import {
  onBeforeUnmount, ref, watch,
} from '@vue/composition-api'

import useWialon from '@/compositions/useWialon'
import { eventBus } from '@/components/Map/EventBus'

import markerIcon from '@/assets/markerIcon'
import markerInstanceIcon from '@/assets/markerInstanceIcon'
import getInstancesInfo from '@/components/Map/utils/getInstancesInfo'
import { getInstanceIconUrl } from '@/utils/instanceGetters'
import { MAP_DEFAULT_COORDS } from '@/components/Map/setupMap'


export default ({
  props: {
    ids: {
      type: Array,
      default: () => [],
    },
    instancesList: Array,
    watchLocation: {
      type: Boolean,
      default: false,
    },
    track: Boolean,
    allowScrollZoom: Boolean,
    noFocus: Boolean,
  },
  setup(props, context) {
    const { emit } = context

    const instances = ref({})

    const defaultCoords = MAP_DEFAULT_COORDS
    const defaultZoom = 9
    const accuracy = 0.001

    const coords = ref(defaultCoords)
    const userCoords = ref({})
    const zoom = ref(defaultZoom)
    const showUserMarker = ref(false)

    const { state, wialon, addLoadListener } = useWialon
    const getUnits = () => {
      const { sess } = state
      const units = sess.getItems('avl_unit')
      if (props.instancesList && units) {
        return units.filter(unit => props.instancesList.some(i => i.wialonId === unit.getId()))
      }
      return units
    }

    const emitUpdate = () => {
      Promise
        .all(getInstancesInfo({ instances: instances.value, wialon: wialon.value }))
        .then(values => emit('updateInfo', values))
    }

    const moveMarker = (e, instance) => {
      const data = e.getData()
      if (!data.pos) return
      instance.coords = [data.pos.y, data.pos.x]
      emitUpdate()
    }

    const getInstanceIcon = (instance) => {
      let icon = markerInstanceIcon
      const currentInstance = props.instancesList.find(i => i.wialonId === instance.getId())
      if (!currentInstance) return icon
      if (!currentInstance.icon) return icon
      icon = {
        layout: 'default#image',
        imageHref: getInstanceIconUrl(currentInstance?.iconUrl),
        imageSize: [70, 70],
        imageOffset: [-30, -30],
      }
      return icon
    }

    const addInstance = (instance) => {
      instance.icon = getInstanceIcon(instance)
      const pos = instance.getPosition()
      if (!pos) return
      instance.coords = [pos.y, pos.x]

      instance.addListener('messageRegistered', e => moveMarker(e, instance))

      // eslint-disable-next-line consistent-return
      return instance
    }

    const setZoom = (value) => {
      zoom.value = value
    }

    const setCoord = async (value) => {
      coords.value = value
      setTimeout(() => {
        coords.value = [coords.value[0] + accuracy, coords.value[1] + accuracy]
      }, 2000)
    }

    const markerClick = (e) => {
      setCoord(e.get('target')
        .geometry
        .getCoordinates())
    }

    const boundsChange = (e) => {
      if (e.get('newZoom') !== e.get('oldZoom')) {
        setZoom(e.get('newZoom'))
      }
    }

    const init = () => {
      const { sess } = state
      if (!sess) return
      const units = getUnits()
      if (!units) return
      instances.value = units.map(unit => addInstance(unit)).filter(Boolean)
      if (!props.track) {
        if (instances.value.length === 1 && !props.noFocus) {
          setCoord(instances.value[0].coords)
        }
      }
      emitUpdate()
    }
    addLoadListener(init)

    watch(() => props.instancesList, () => {
      init()
    }, { immediate: true })

    onBeforeUnmount(() => {
      if (!instances.value.length) return
      instances.value.forEach(instance => e => moveMarker(e, instance))
    })

    if (props.watchLocation) {
      eventBus.$on('update-location', (e) => {
        if (!e) return
        if (!e.position) {
          setZoom(defaultZoom)
          return
        }
        const { lat, lon } = e.position
        userCoords.value = [parseFloat(lon), parseFloat(lat)]
        setCoord(userCoords.value)
        showUserMarker.value = true
      })
    }

    const setupMapInstance = (map) => {
      if (!props.allowScrollZoom) map.behaviors.disable('scrollZoom')
      emit('mapWasInitialized', map)
    }

    return {
      coords,
      userCoords,
      zoom,
      markerIcon,
      showUserMarker,
      instances,
      setupMapInstance,
      markerClick,
      boundsChange,
      init,
    }
  },
})
